import { useCallback, useEffect, useRef, useState } from 'react';
import { apiUrl, authFetch, authHeaders } from '../config/apiClient';
import { readOfflineData, writeOfflineData } from '../offlineDataCache';
import {
  buildMapDataParams,
  buildMapDataCacheKey,
  mergeMapData,
  normalizeMapData,
  normalizeQualityReport,
  resolveMapDataResponse,
  splitMapDataPayload,
  withConditionalEtag,
  MAP_HYDRATE_ROUTE_LIMIT,
  MAP_LOAD_PHASES,
  EMPTY_DATA_QUALITY_REPORT,
} from '../mapDataFetch';
import { t } from '../i18n';

const EMPTY_FETCHED_DATA = {
  checkins: [],
  health: [],
  moves: [],
  movies: [],
  shows: [],
  diary: [],
  concerts: [],
  googleMaps: [],
  spotify: [],
  appleMusic: [],
  photos: [],
  music: null,
  mediaAggregates: false,
};

export function useMapData({ user, clearSession, currentFilters }) {
  const [allFetchedData, setAllFetchedData] = useState(EMPTY_FETCHED_DATA);
  const [dataQualityReport, setDataQualityReport] = useState(EMPTY_DATA_QUALITY_REPORT);
  const [loading, setLoading] = useState(false);
  const [mapLoadPhase, setMapLoadPhase] = useState(null);
  const mapFetchAbortRef = useRef(null);
  const [fetchError, setFetchError] = useState(null);
  const [offlineCachedAt, setOfflineCachedAt] = useState(null);

  const fetchDataFromBackend = useCallback(async (filters, signal) => {
    setLoading(true);
    setFetchError(null);
    setOfflineCachedAt(null);
    setMapLoadPhase(MAP_LOAD_PHASES.SHELL);

    const shellParams = buildMapDataParams(filters, { includePoints: false });
    const fullParams = buildMapDataParams(filters, {
      includePoints: true,
      limit: MAP_HYDRATE_ROUTE_LIMIT,
      includeQuality: true,
    });
    const cacheKey = buildMapDataCacheKey(filters);

    try {
      const cached = await readOfflineData(user, cacheKey);
      const baseHeaders = authHeaders();

      const shellResponse = await authFetch(
        apiUrl(`/map-data?${shellParams.toString()}`),
        { headers: baseHeaders, signal },
      );
      if (shellResponse.status === 401) {
        clearSession();
        return;
      }
      if (!shellResponse.ok) throw new Error(`HTTP error! status: ${shellResponse.status}`);
      const shellData = normalizeMapData(await shellResponse.json());
      if (signal?.aborted) return;
      setAllFetchedData(shellData);

      setMapLoadPhase(MAP_LOAD_PHASES.DETAILS);
      const fullResponse = await authFetch(
        apiUrl(`/map-data?${fullParams.toString()}`),
        {
          headers: withConditionalEtag(baseHeaders, cached?.etag),
          signal,
        },
      );
      if (fullResponse.status === 401) {
        clearSession();
        return;
      }

      const resolved = await resolveMapDataResponse(fullResponse, cached);
      if (signal?.aborted) return;

      if (resolved.status === 304) {
        setAllFetchedData(resolved.data);
        setDataQualityReport(
          resolved.quality
            ? normalizeQualityReport(resolved.quality)
            : EMPTY_DATA_QUALITY_REPORT,
        );
        return;
      }

      let fullPayload = resolved.data;
      const hydrated = splitMapDataPayload(fullPayload);
      const normalizedQuality = hydrated.quality
        ? normalizeQualityReport(hydrated.quality)
        : EMPTY_DATA_QUALITY_REPORT;
      // Keep hydrate capped at MAP_HYDRATE_ROUTE_LIMIT. A third unlimited
      // include_points=1 fetch (old truncated path) freezes the map on
      // multi-year ranges. pagination.truncated still signals the cap.
      if (signal?.aborted) return;

      const { data: fullData } = splitMapDataPayload(fullPayload);
      const normalizedData = mergeMapData(shellData, fullData);
      await writeOfflineData(user, cacheKey, {
        data: normalizedData,
        quality: normalizedQuality,
        etag: resolved.etag,
      });
      setAllFetchedData(normalizedData);
      setDataQualityReport(normalizedQuality);
    } catch (error) {
      if (error.name === 'AbortError' || signal?.aborted) {
        return;
      }
      const offline = await readOfflineData(user, cacheKey);
      if (offline) {
        setAllFetchedData(offline.data);
        setDataQualityReport(offline.quality);
        setOfflineCachedAt(offline.cachedAt || null);
        setFetchError(t('web.fetch.offlineCached'));
        return;
      }
      console.error('Error fetching data from backend:', error);
      setAllFetchedData({
        checkins: [],
        health: [],
        moves: [],
        movies: [],
        shows: [],
        diary: [],
        concerts: [],
        googleMaps: [],
        spotify: [],
        appleMusic: [],
        music: null,
        mediaAggregates: false,
      });
      setDataQualityReport(EMPTY_DATA_QUALITY_REPORT);
      setFetchError(t('web.fetch.failed'));
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
        setMapLoadPhase(null);
      }
    }
  }, [clearSession, user]);

  const cancelMapFetch = useCallback(() => {
    mapFetchAbortRef.current?.abort();
    setLoading(false);
    setMapLoadPhase(null);
  }, []);

  useEffect(() => {
    if (user) {
      const controller = new AbortController();
      mapFetchAbortRef.current = controller;
      fetchDataFromBackend(currentFilters, controller.signal);
      return () => {
        controller.abort();
        if (mapFetchAbortRef.current === controller) {
          mapFetchAbortRef.current = null;
        }
      };
    }
    return undefined;
  }, [currentFilters, fetchDataFromBackend, user]);

  const retryFetch = useCallback(() => {
    fetchDataFromBackend(currentFilters);
  }, [fetchDataFromBackend, currentFilters]);

  return {
    allFetchedData,
    dataQualityReport,
    loading,
    mapLoadPhase,
    fetchError,
    offlineCachedAt,
    fetchDataFromBackend,
    cancelMapFetch,
    retryFetch,
  };
}
